import type React from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@legal-500/legal500-design-system";
import styles from "./AsyncSearchableSelect.module.css";

// TODO: Move this functionality into @legal-500/legal500-design-system SearchableSelect component
// This is a temporary implementation until the design system supports async/remote data fetching

export type AsyncSelectOption = {
  value: string | number | null;
  label: string;
  description?: string;
};

type AsyncSearchableSelectProps = {
  name: string;
  label: string;
  required?: boolean;
  value: string | number | null;
  onChange: (value: string | number | null) => void;
  onOptionChange?: (option: AsyncSelectOption | null) => void;
  onSearch: (query: string) => Promise<AsyncSelectOption[]>;
  disabled?: boolean;
  placeholder?: string;
  helperText?: string;
  error?: string;
  debounceMs?: number;
  minSearchLength?: number;
  initialOption?: AsyncSelectOption | null;
  /** Offer the clear (✕) affordance. Set false where the field must always hold a value. */
  clearable?: boolean;
  onLoadMore?: () => Promise<{ options: AsyncSelectOption[]; hasMore: boolean }>;
};

export function AsyncSearchableSelect({
  name,
  label,
  required = false,
  value,
  onChange,
  onOptionChange,
  onSearch,
  disabled = false,
  placeholder = "Search...",
  helperText,
  error,
  debounceMs = 300,
  minSearchLength = 2,
  initialOption = null,
  clearable = true,
  onLoadMore,
}: AsyncSearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [options, setOptions] = useState<AsyncSelectOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<AsyncSelectOption | null>(initialOption);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<Map<number, HTMLLIElement>>(new Map());
  const debounceTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const performSearch = useCallback(
    async (query: string) => {
      if (query.length < minSearchLength) {
        setOptions([]);
        setHighlightedIndex(-1);
        setHasMore(false);
        return;
      }

      setIsLoading(true);
      setHasMore(false);
      try {
        const results = await onSearch(query);
        setOptions(results);
        setHighlightedIndex(-1);
        if (onLoadMore && results.length > 0) setHasMore(true);
      } catch (error) {
        console.error("Search failed:", error);
        setOptions([]);
        setHighlightedIndex(-1);
      } finally {
        setIsLoading(false);
      }
    },
    [onSearch, onLoadMore, minSearchLength],
  );

  const handleLoadMore = useCallback(async () => {
    if (!onLoadMore || loadingMore) return;
    setLoadingMore(true);
    try {
      const result = await onLoadMore();
      setOptions(prev => [...prev, ...result.options]);
      setHasMore(result.hasMore);
    } catch (err) {
      console.error("Load more failed:", err);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [onLoadMore, loadingMore]);

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      performSearch(query);
    }, debounceMs);
  };

  const handleSelect = (option: AsyncSelectOption) => {
    setSelectedOption(option);
    onChange(option.value);
    onOptionChange?.(option);
    setIsOpen(false);
    setSearchQuery("");
    setHighlightedIndex(-1);
  };

  const clearSelectedOption = () => {
    setSelectedOption(null);
    onChange(null);
    onOptionChange?.(null);
  };

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          if (options.length > 0) {
            setHighlightedIndex(prev => (prev + 1) % options.length);
          }
          break;
        case "ArrowUp":
          event.preventDefault();
          if (options.length > 0) {
            setHighlightedIndex(prev => (prev - 1 + options.length) % options.length);
          }
          break;
        case "Enter":
          event.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < options.length) {
            handleSelect(options[highlightedIndex]);
          }
          break;
        case "Escape":
          event.preventDefault();
          setIsOpen(false);
          setSearchQuery("");
          setHighlightedIndex(-1);
          break;
        case "Tab":
          setIsOpen(false);
          setSearchQuery("");
          setHighlightedIndex(-1);
          break;
      }
    },
    [isOpen, options, highlightedIndex],
  );

  useEffect(() => {
    optionRefs.current.get(highlightedIndex)?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  // Sync selectedOption when initialOption changes — including when it becomes null
  // (e.g. parent cleared the value because a cascading dependency changed).
  useEffect(() => {
    setSelectedOption(initialOption);
  }, [initialOption]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    // When minSearchLength is 0, populate the initial list on open so the user
    // sees options without having to type. Consumer's onSearch must accept an
    // empty query and return its first page.
    if (isOpen && minSearchLength === 0) {
      performSearch("");
    }
  }, [isOpen, minSearchLength, performSearch]);

  // Calculate fixed position for the dropdown based on container's bounding rect
  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownHeight = 300;

    const position: "bottom" | "top" =
      spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? "top" : "bottom";

    if (position === "bottom") {
      setDropdownStyle({
        position: "fixed",
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    } else {
      setDropdownStyle({
        position: "fixed",
        bottom: window.innerHeight - rect.top + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideContainer = containerRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideContainer && !insideDropdown) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isOpen && containerRef.current) {
      updateDropdownPosition();
    }
  }, [isOpen, updateDropdownPosition]);

  // Reposition dropdown on scroll (capture phase for any ancestor) and resize
  useEffect(() => {
    if (!isOpen) return;

    const handleScrollOrResize = () => {
      updateDropdownPosition();
    };

    window.addEventListener("scroll", handleScrollOrResize, { capture: true, passive: true });
    window.addEventListener("resize", handleScrollOrResize, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, {
        capture: true,
      } as EventListenerOptions);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isOpen, updateDropdownPosition]);

  const displayValue = selectedOption?.label || placeholder;
  const errorId = `${name}-error`;
  const helperId = `${name}-helper`;
  const describedBy = error ? errorId : helperText ? helperId : undefined;

  return (
    <div className={styles.formField}>
      <label htmlFor={name} className={styles.label}>
        {label}{" "}
        {required && (
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        )}
      </label>

      <div ref={containerRef} className={styles.selectContainer}>
        <button
          type="button"
          id={name}
          className={styles.selectButton}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-describedby={describedBy}
          aria-invalid={!!error}
        >
          <span className={selectedOption ? styles.value : styles.placeholder}>{displayValue}</span>
          <span className={styles.actions}>
            {clearable && selectedOption && !disabled && (
              <span
                className={styles.clearButton}
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  clearSelectedOption();
                }}
                role="button"
                aria-label="Clear selection"
              >
                <Icon icon="cross" size="small" />
              </span>
            )}
            <Icon
              icon={isOpen ? "chevron-up" : "chevron-down"}
              size="small"
              className={styles.arrow}
            />
          </span>
        </button>
      </div>

      {isOpen &&
        createPortal(
          <div ref={dropdownRef} className={styles.dropdown} style={dropdownStyle}>
            <div className={styles.searchContainer}>
              <input
                ref={searchInputRef}
                type="text"
                className={styles.searchInput}
                placeholder={
                  minSearchLength === 0
                    ? "Search..."
                    : `Type to search (min ${minSearchLength} chars)...`
                }
                value={searchQuery}
                onChange={e => handleSearchChange(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>

            <ul className={styles.optionsList} role="listbox">
              {isLoading ? (
                <li className={styles.loading}>Searching...</li>
              ) : searchQuery.length < minSearchLength ? (
                <li className={styles.noResults}>
                  Type at least {minSearchLength} characters to search
                </li>
              ) : options.length === 0 ? (
                <li className={styles.noResults}>No results found</li>
              ) : (
                <>
                  {options.map((option, index) => (
                    <li
                      key={String(option.value)}
                      ref={el => {
                        if (el) optionRefs.current.set(index, el);
                      }}
                      className={`${styles.option} ${
                        option.value === value ? styles.optionSelected : ""
                      } ${index === highlightedIndex ? styles.optionHighlighted : ""}`}
                      onClick={() => handleSelect(option)}
                      role="option"
                      aria-selected={option.value === value}
                    >
                      <div className={styles.optionContent}>
                        <span className={styles.optionLabel}>{option.label}</span>
                        {option.description && (
                          <span className={styles.optionDescription}>{option.description}</span>
                        )}
                      </div>
                    </li>
                  ))}
                  {hasMore && (
                    <li className={styles.loadMore}>
                      <button
                        type="button"
                        className={styles.loadMoreButton}
                        onClick={e => {
                          e.stopPropagation();
                          handleLoadMore();
                        }}
                        disabled={loadingMore}
                      >
                        {loadingMore ? "Loading..." : "Load more"}
                      </button>
                    </li>
                  )}
                </>
              )}
            </ul>
          </div>,
          document.body,
        )}

      {error && (
        <span id={errorId} className={styles.errorText} role="alert" aria-live="polite">
          {error}
        </span>
      )}
      {helperText && !error && (
        <span id={helperId} className={styles.helperText}>
          {helperText}
        </span>
      )}
    </div>
  );
}
